import { LazyQueryExecFunction } from '@apollo/client'

import { HookMethodArgs } from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/types/types'
import { calculateCollectionPreorderDateTime } from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/utils/calculateCollectionPreorderDateTime'
import { calculateDeliveryPreorderWindow } from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/utils/calculateDeliveryPreorderWindow'
import {
  OutletAvailabilityQuery,
  OutletAvailabilityQueryVariables,
} from '@src/hooks/sharedQueries/outletAvailabilityQuery/queries/__generated__/OutletAvailability.graphql-interface'
import { FulfilmentFilterWhenType } from '@src/hooks/useFulfilmentFilter/validation'
import { debugLogger } from '@src/utils/debugLogger'
import { parseOutletDates } from '@src/utils/fulfilmentTimes/parsers'
import { ParsedDeliveryPreorderWindow } from '@src/utils/fulfilmentTimes/types'

import { calculateFulfilmentTimesFromASAPTime } from './calculateFulfilmentTimesFromASAPTime'

/** Customer has a preorder time. If it has expired:
 *  + and the outlet offers asap and is still within the ordering times then set to asap
 *  + and the outlet does not offer asap, or is outside the ordering times, then:
 *    ~ if the outlet has preorder times available, set to the next available time
 *    ~ if the outlet has no preorder times available, then refetch the outlet to update the ui to closed
 **/
export const calculateFulfilmentTimesFromPreorderTimes = async ({
  hookMethodArgs,
  existingCollectionPreorderDatetime,
  existingDeliveryPreorderWindow,
}: {
  hookMethodArgs: HookMethodArgs & {
    outletAvailabilityQuery: LazyQueryExecFunction<
      OutletAvailabilityQuery,
      OutletAvailabilityQueryVariables
    >
  }
  existingCollectionPreorderDatetime: Date | null
  existingDeliveryPreorderWindow: Pick<
    ParsedDeliveryPreorderWindow,
    'start' | 'end'
  > | null
}): Promise<{
  // null means asap, Date means preorder, undefined means no change
  collectionPreorderDatetime?: Date | null
  deliveryPreorderWindow?: Pick<
    ParsedDeliveryPreorderWindow,
    'start' | 'end'
  > | null
}> => {
  if (!existingCollectionPreorderDatetime && !existingDeliveryPreorderWindow) {
    // eslint-disable-next-line no-console
    console.error(
      'calculateFulfilmentTimeFromPreorderTime called without existing preorder times. `calculateFulfilmentTimesFromASAPTime` should be used instead'
    )
    return calculateFulfilmentTimesFromASAPTime(hookMethodArgs)
  }

  const nextAvailableCollectionPreorderDatetime =
    calculateCollectionPreorderDateTime({
      outlet: hookMethodArgs.outlet,
      fulfilmentFilterWhen: {
        type: FulfilmentFilterWhenType.PREORDER,
        preorderDate: existingCollectionPreorderDatetime || new Date(),
      },
    })
  const nextAvailableDeliveryPreorderWindow = calculateDeliveryPreorderWindow({
    outlet: hookMethodArgs.outlet,
    fulfilmentFilterWhen: {
      type: FulfilmentFilterWhenType.PREORDER,
      preorderDate: existingDeliveryPreorderWindow?.start || new Date(),
    },
  })

  // collection times expired if:
  // - no collection time could be calculated from the current outlet data
  // - next collection time is different to existing collection time
  const collectionTimeHasExpired =
    !nextAvailableCollectionPreorderDatetime ||
    (existingCollectionPreorderDatetime &&
      nextAvailableCollectionPreorderDatetime.valueOf() !==
        existingCollectionPreorderDatetime.valueOf())
  // delivery times expired if:
  // - no delivery time could be calculated from the current outlet data
  // - next delivery time is different to existing delivery time
  const deliveryTimeHasExpired =
    !nextAvailableDeliveryPreorderWindow ||
    (existingDeliveryPreorderWindow &&
      nextAvailableDeliveryPreorderWindow.start.valueOf() !==
        existingDeliveryPreorderWindow.start.valueOf())

  if (!collectionTimeHasExpired && !deliveryTimeHasExpired) {
    debugLogger('Fulfilment times are still valid', {
      existingCollectionPreorderDatetime,
      existingDeliveryPreorderWindow,
      nextAvailableCollectionPreorderDatetime,
      nextAvailableDeliveryPreorderWindow,
    })
    return {
      collectionPreorderDatetime: existingCollectionPreorderDatetime,
      deliveryPreorderWindow: existingDeliveryPreorderWindow,
    }
  }

  if (
    nextAvailableCollectionPreorderDatetime ||
    nextAvailableDeliveryPreorderWindow
  ) {
    debugLogger('Fulfilment times have expired. Updating with new times', {
      nextAvailableCollectionPreorderDatetime,
      nextAvailableDeliveryPreorderWindow,
    })
    return {
      collectionPreorderDatetime: collectionTimeHasExpired
        ? nextAvailableCollectionPreorderDatetime
        : undefined,
      deliveryPreorderWindow: deliveryTimeHasExpired
        ? nextAvailableDeliveryPreorderWindow
        : undefined,
    }
  }

  debugLogger('No fulfilment times available. Refetching outlet')
  const { data } = await hookMethodArgs.outletAvailabilityQuery({
    variables: {
      outletId: hookMethodArgs.outlet.id,
      fulfilmentMethod:
        hookMethodArgs.existingData.currentFulfilment.narrowType,
      fulfilmentLocation:
        hookMethodArgs.existingData.currentFulfilment.location,
    },
    fetchPolicy: 'network-only',
  })

  if (!data?.outlet) {
    debugLogger('No outlet data returned. Unable to update fulfilment times')
    return {
      collectionPreorderDatetime: existingCollectionPreorderDatetime,
      deliveryPreorderWindow: existingDeliveryPreorderWindow,
    }
  }

  const updatedOutlet = parseOutletDates(data.outlet)

  if (
    updatedOutlet.orderability.prioritisedCurrentlyAvailableFulfilmentMethods.includes(
      hookMethodArgs.existingData.currentFulfilment.narrowType
    )
  ) {
    const nextCollectionPreorderDatetime = calculateCollectionPreorderDateTime({
      outlet: updatedOutlet,
      fulfilmentFilterWhen: {
        type: FulfilmentFilterWhenType.ANYTIME,
      },
    })
    const nextDeliveryPreorderWindow = calculateDeliveryPreorderWindow({
      outlet: updatedOutlet,
      fulfilmentFilterWhen: {
        type: FulfilmentFilterWhenType.ANYTIME,
      },
    })
    debugLogger('Calculated updated fulfilment time from new data', {
      nextCollectionPreorderDatetime,
      nextDeliveryPreorderWindow,
    })
    return {
      collectionPreorderDatetime: nextCollectionPreorderDatetime,
      deliveryPreorderWindow: nextDeliveryPreorderWindow,
    }
  }

  debugLogger('Outlet is unorderable. Unable to update fulfilment times')
  return {
    collectionPreorderDatetime: existingCollectionPreorderDatetime,
    deliveryPreorderWindow: existingDeliveryPreorderWindow,
  }
}
